import type {
  StudyMissionSelectDto,
  StudyRunCaseDashboardMissionDto,
  StudyRunCaseDashboardThreadDetailDto,
  StudyRunCaseDashboardThreadVerdictDto,
  StudyRunCaseSelectDto,
  StudyScoringOutputSelectDto,
} from '@@types/api.types';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDefaultActiveThreadId } from './meia-p4-run-cases.util';
import { getDefaultBriefRunCaseId } from './meia-p5-brief.util';

export type BriefRunCaseSelectionMode = 'latest' | 'selectable';

type UseMeiaP5BriefSelectionParams = {
  dashboardMissions: StudyRunCaseDashboardMissionDto[];
  dashboardSelectedMissionId: string | null | undefined;
  forcedMissionId?: string;
  missions: StudyMissionSelectDto[];
  runCaseSelectionMode?: BriefRunCaseSelectionMode;
  runCases: StudyRunCaseSelectDto[];
  scoringOutputs: StudyScoringOutputSelectDto[];
  syncMissionParam?: boolean;
  syncScopedParams?: boolean;
};

type UseMeiaP5BriefSelectionResult = {
  activeMissionDashboard: StudyRunCaseDashboardMissionDto | null;
  activeThreadDashboard: StudyRunCaseDashboardThreadDetailDto | null;
  activeThreadId: string | null;
  activeThreadVerdict: StudyRunCaseDashboardThreadVerdictDto | null;
  handleSelectMission: (missionId: string) => void;
  handleSelectRunCase: (runCaseId: string) => void;
  handleSelectThread: (threadId: string) => void;
  selectedMissionId: string | null;
  selectedRunCase: StudyRunCaseSelectDto | null;
  selectedRunCaseId: string | null;
  selectedScoringOutput: StudyScoringOutputSelectDto | null;
};

export const useRequestedRunCaseId = (): string | null => {
  const [searchParams] = useSearchParams();
  return searchParams.get('runCaseId');
};

export const resolveSelectedRunCaseId = (params: {
  requestedRunCaseId: string | null;
  runCaseSelectionMode?: BriefRunCaseSelectionMode;
  runCases: StudyRunCaseSelectDto[];
  scoringOutputs: StudyScoringOutputSelectDto[];
}): string | null => {
  const { requestedRunCaseId, runCaseSelectionMode = 'selectable', runCases, scoringOutputs } = params;
  const defaultRunCaseId = getDefaultBriefRunCaseId({ runCases, scoringOutputs });
  return runCaseSelectionMode === 'selectable' &&
    requestedRunCaseId &&
    runCases.some((runCase) => runCase.id === requestedRunCaseId)
    ? requestedRunCaseId
    : defaultRunCaseId;
};

export const useMeiaP5BriefSelection = (params: UseMeiaP5BriefSelectionParams): UseMeiaP5BriefSelectionResult => {
  const {
    dashboardMissions,
    dashboardSelectedMissionId,
    forcedMissionId,
    missions,
    runCaseSelectionMode = 'selectable',
    runCases,
    scoringOutputs,
    syncMissionParam = true,
    syncScopedParams = true,
  } = params;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMissionId = searchParams.get('missionId');
  const requestedRunCaseId = searchParams.get('runCaseId');
  const requestedThreadId = searchParams.get('threadId');

  const selectedRunCaseId = useMemo(
    () => resolveSelectedRunCaseId({ requestedRunCaseId, runCaseSelectionMode, runCases, scoringOutputs }),
    [requestedRunCaseId, runCaseSelectionMode, runCases, scoringOutputs],
  );
  const selectedRunCase = runCases.find((runCase) => runCase.id === selectedRunCaseId) ?? null;
  const selectedScoringOutput = getLatestScoringOutput({ runCaseId: selectedRunCaseId, scoringOutputs });

  const selectedMissionId = resolveSelectedMissionId({
    dashboardSelectedMissionId,
    forcedMissionId,
    missions,
    requestedMissionId,
  });
  const activeMissionDashboard = getActiveMissionDashboard({
    dashboardMissions,
    selectedMissionId,
  });
  const activeThreadId = getActiveThreadId({
    activeMissionDashboard,
    requestedThreadId,
  });
  const activeThreadDashboard = useMemo(
    () => getActiveThreadDashboard({ activeMissionDashboard, activeThreadId }),
    [activeMissionDashboard, activeThreadId],
  );
  const activeThreadVerdict = useMemo(
    () => getActiveThreadVerdict({ activeMissionDashboard, activeThreadId }),
    [activeMissionDashboard, activeThreadId],
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        let changed = false;

        if (selectedRunCaseId && next.get('runCaseId') !== selectedRunCaseId) {
          next.set('runCaseId', selectedRunCaseId);
          changed = true;
        }
        if ((!syncScopedParams || !syncMissionParam) && next.has('missionId')) {
          next.delete('missionId');
          changed = true;
        }
        if (!syncScopedParams && next.has('threadId')) {
          next.delete('threadId');
          changed = true;
        }
        if (syncScopedParams && syncMissionParam && selectedMissionId && next.get('missionId') !== selectedMissionId) {
          next.set('missionId', selectedMissionId);
          changed = true;
        }
        if (syncScopedParams && activeThreadId && next.get('threadId') !== activeThreadId) {
          next.set('threadId', activeThreadId);
          changed = true;
        }
        if (syncScopedParams && !activeThreadId && next.has('threadId')) {
          next.delete('threadId');
          changed = true;
        }
        return changed ? next : prev;
      },
      { preventScrollReset: true, replace: true },
    );
  }, [activeThreadId, selectedMissionId, selectedRunCaseId, setSearchParams, syncMissionParam, syncScopedParams]);

  const handleSelectMission = (missionId: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('missionId', missionId);
        next.delete('threadId');
        return next;
      },
      { preventScrollReset: true, replace: false },
    );
  };

  const handleSelectRunCase = (runCaseId: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('runCaseId', runCaseId);
        next.delete('missionId');
        next.delete('threadId');
        return next;
      },
      { preventScrollReset: true, replace: false },
    );
  };

  const handleSelectThread = (threadId: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('threadId', threadId);
        return next;
      },
      { preventScrollReset: true, replace: false },
    );
  };

  return {
    activeMissionDashboard,
    activeThreadDashboard,
    activeThreadId,
    activeThreadVerdict,
    handleSelectMission,
    handleSelectRunCase,
    handleSelectThread,
    selectedMissionId,
    selectedRunCase,
    selectedRunCaseId,
    selectedScoringOutput,
  };
};

const resolveSelectedMissionId = (params: {
  dashboardSelectedMissionId: string | null | undefined;
  forcedMissionId?: string;
  missions: StudyMissionSelectDto[];
  requestedMissionId: string | null;
}): string | null => {
  const { dashboardSelectedMissionId, forcedMissionId, missions, requestedMissionId } = params;
  if (forcedMissionId && missions.some((mission) => mission.id === forcedMissionId)) return forcedMissionId;
  if (requestedMissionId && missions.some((mission) => mission.id === requestedMissionId)) return requestedMissionId;
  return dashboardSelectedMissionId ?? missions[0]?.id ?? null;
};

const getActiveMissionDashboard = (params: {
  dashboardMissions: StudyRunCaseDashboardMissionDto[];
  selectedMissionId: string | null;
}): StudyRunCaseDashboardMissionDto | null => {
  const { dashboardMissions, selectedMissionId } = params;
  return dashboardMissions.find((mission) => mission.missionId === selectedMissionId) ?? dashboardMissions[0] ?? null;
};

const getActiveThreadDashboard = (params: {
  activeMissionDashboard: StudyRunCaseDashboardMissionDto | null;
  activeThreadId: string | null;
}): StudyRunCaseDashboardThreadDetailDto | null => {
  const { activeMissionDashboard, activeThreadId } = params;
  if (!activeMissionDashboard) return null;
  return activeMissionDashboard.threads.find((thread) => thread.threadId === activeThreadId) ?? null;
};

const getActiveThreadId = (params: {
  activeMissionDashboard: StudyRunCaseDashboardMissionDto | null;
  requestedThreadId: string | null;
}): string | null => {
  const { activeMissionDashboard, requestedThreadId } = params;
  const threadVerdicts = activeMissionDashboard?.threadVerdicts ?? [];
  if (requestedThreadId && threadVerdicts.some((thread) => thread.threadId === requestedThreadId))
    return requestedThreadId;
  return getDefaultActiveThreadId(threadVerdicts);
};

const getActiveThreadVerdict = (params: {
  activeMissionDashboard: StudyRunCaseDashboardMissionDto | null;
  activeThreadId: string | null;
}): StudyRunCaseDashboardThreadVerdictDto | null => {
  const { activeMissionDashboard, activeThreadId } = params;
  return activeMissionDashboard?.threadVerdicts.find((thread) => thread.threadId === activeThreadId) ?? null;
};

const getLatestScoringOutput = (params: {
  runCaseId: string | null;
  scoringOutputs: StudyScoringOutputSelectDto[];
}): StudyScoringOutputSelectDto | null => {
  const { runCaseId, scoringOutputs } = params;
  if (!runCaseId) return null;
  return (
    scoringOutputs
      .filter((output) => output.runCaseId === runCaseId)
      .sort((a, b) => new Date(b.scoredAt).getTime() - new Date(a.scoredAt).getTime())[0] ?? null
  );
};
